import { useCallback, useMemo } from 'react';

import { SelectedReceiver } from '../../components/transaction/SelectedReceiver';
import { useConfig } from '../../defaultConfig';
import { useAvtalegiro, useAvtalegiroDetails } from '../../fetchHooks/useAvtalegiro';
import type { AvtalegiroAgreement, AvtalegiroAgreementDetails, PaymentItem } from '../../generated-types';

type Status = 'success' | 'error' | 'not-found';

type GuessAvtalegiroResponse = {
    optimisticAvtalegiro: AvtalegiroAgreement | undefined;
    optimisticAvtalegiroDetails: AvtalegiroAgreementDetails | undefined;
    status: Status;
    isLoading: boolean;
    invalidateQuery: () => void;
};

const determineStatus = (optmisticAvtalegiros: AvtalegiroAgreement[]): Status => {
    if (optmisticAvtalegiros.length === 0) return 'not-found';
    return 'success';
};

const useGuessAvtalegiroFromStoppedPayment = (
    paymentItem: PaymentItem | undefined,
    toAccount: SelectedReceiver | undefined,
): GuessAvtalegiroResponse => {
    const { featureToggles } = useConfig();
    const enabled = useMemo(
        () =>
            paymentItem?.paymentItemType === 'AUTO_PAYMENT' &&
            paymentItem.subStatus === 'EXCEEDING_MAX_LIMIT' &&
            Boolean(toAccount?.accountNumber.value) &&
            featureToggles.enableStoppedAvtalegiroEdit,
        [paymentItem, toAccount, featureToggles.enableStoppedAvtalegiroEdit],
    );

    const { data: avtalegiros = [], isLoading, isError, invalidateQuery } = useAvtalegiro(enabled);

    const { status, optimisticAvtalegiro } = useMemo((): {
        optimisticAvtalegiro?: AvtalegiroAgreement;
        status: Status;
    } => {
        if (!featureToggles.enableStoppedAvtalegiroEdit) return { status: 'not-found' };
        if (!avtalegiros || avtalegiros.length === 0) return { status: 'not-found' };
        if (!paymentItem?.receiver?.accountNumber?.value || !paymentItem.kid?.value) return { status: 'not-found' };

        const optimisticAvtalegiros = avtalegiros.filter(
            (avtalegiro) =>
                avtalegiro.toAccount?.value === paymentItem?.receiver?.accountNumber?.value &&
                avtalegiro.kid?.value === paymentItem.kid?.value,
        );
        return {
            optimisticAvtalegiro: optimisticAvtalegiros?.[0],
            status: determineStatus(optimisticAvtalegiros),
        };
    }, [avtalegiros, paymentItem, featureToggles.enableStoppedAvtalegiroEdit]);

    const {
        data: optimisticAvtalegiroDetails,
        isLoading: detailsLoading,
        isError: detailsError,
        invalidateQuery: invalidateDetailsQuery,
    } = useAvtalegiroDetails(optimisticAvtalegiro?.id);

    const invalidate = useCallback(() => {
        invalidateQuery();
        invalidateDetailsQuery();
    }, [invalidateDetailsQuery, invalidateQuery]);

    return {
        optimisticAvtalegiro,
        optimisticAvtalegiroDetails,
        status: isError || detailsError ? 'error' : status,
        isLoading: detailsLoading || isLoading,
        invalidateQuery: invalidate,
    };
};

export default useGuessAvtalegiroFromStoppedPayment;
